// Centrale stijldefinitie voor gebiedtypes — gebruikt door zowel de RDKaart-overlay
// als de chips in PutDetail. Eén bron van waarheid voor kleur per type.
//
// Sinds DB-migratie: leest stijlen uit de styling-cache (gevuld door
// getStyling() bij app-start). Hardcoded GEBIED_STIJL is nu fallback
// voor: (1) eerste milliseconden voordat preload klaar is, (2) types
// die nog niet in de DB-styling staan.

import { getStylingSync } from '../api/api';

// Hardcoded fallback — bewaard voor types die niet in de DB staan en
// als noodgreep tijdens app-startup.
export const GEBIED_STIJL = {
  beschermd: {
    label: 'beschermd gebied',
    kort: 'Beschermd',
    fillRgba: 'rgba(255, 149, 0, 0.28)',
    stroke: '#ff9500',
    chipBg: '#fff1de',
    chipText: '#a35a00',
    pattern: null,
  },
  beperkt_beschermd: {
    label: 'beperkt beschermd gebied',
    kort: 'Beperkt B.',
    fillRgba: 'rgba(255, 192, 211, 0.42)',
    stroke: '#e58fa0',
    chipBg: '#ffeaef',
    chipText: '#a64862',
    pattern: null,
  },
  attentie: {
    label: 'attentiegebied',
    kort: 'Attentie',
    fillRgba: 'rgba(168, 213, 240, 0.42)',
    stroke: '#5a9fd4',
    chipBg: '#e6f1fa',
    chipText: '#2c5e87',
    pattern: null,
  },
  natura2000: {
    label: 'invloedsgebied Natura 2000',
    kort: 'N2000',
    fillRgba: 'rgba(110, 110, 115, 0.10)',
    stroke: '#6e6e73',
    chipBg: '#eeeef0',
    chipText: '#3d3d42',
    pattern: 'diagonalStripes',
  },
  ambtsgebied: {
    label: 'buiten ambtsgebied',
    kort: 'Buiten ambt',
    fillRgba: 'rgba(110, 110, 115, 0.45)',
    stroke: '#6e6e73',
    chipBg: '#eeeef0',
    chipText: '#3d3d42',
    pattern: null,
    inverse: true,
  },
  invloedsgebied: {
    label: 'invloedsgebied',
    fillRgba: 'rgba(0, 113, 227, 0.15)',
    stroke: '#0071e3',
    chipBg: '#e6f0fb',
    chipText: '#003c7a',
    pattern: null,
  },
  overig: {
    label: 'overig',
    fillRgba: 'rgba(110, 110, 115, 0.12)',
    stroke: '#6e6e73',
    chipBg: '#eeeef0',
    chipText: '#3d3d42',
    pattern: null,
  },
};

/* HEX (#rrggbb) + opacity → rgba string. */
function hexNaarRgba(hex, opacity) {
  if (!hex || hex.length < 7) return `rgba(0,0,0,${opacity})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/* Maak een hex-kleur donkerder voor goed leesbaar chip-tekst-contrast op een
   lichte chip-achtergrond. Factor 0 = onveranderd, 1 = zwart. */
function donkerderHex(hex, factor = 0.5) {
  if (!hex || hex.length < 7) return '#3d3d42';
  const f = Math.max(0, Math.min(1, factor));
  const ch = (i) => Math.round(parseInt(hex.slice(i, i + 2), 16) * (1 - f))
    .toString(16).padStart(2, '0');
  return `#${ch(1)}${ch(3)}${ch(5)}`;
}

/* Mapping van DB-patroonvelden naar oude OpenLayers-pattern-keys. */
const PATROON_MAP = {
  diagonaal_strepen: 'diagonalStripes',
  kruislings: 'crossHatch',
  stippen: 'dots',
};

/* Probeer een styling-row uit de DB-cache te halen; null als niet aanwezig. */
function uitCache(type) {
  const data = getStylingSync();
  if (!data) return null;
  return data.find(r => r.type === type) || null;
}

/* Centrale stijl-getter: probeer eerst DB-cache, val terug op hardcoded. */
export function gebiedStijl(type) {
  const row = uitCache(type);
  if (row) {
    // Auto-bereken chipBg en chipText vanuit kaartstyling zodat chips
    // visueel aansluiten op de overlay-kleur in de kaart.
    // chipBg = dezelfde kleur als de overlay maar met lichte vaste opacity,
    // chipText = donkerder versie van de stroke voor goed contrast.
    return {
      label: row.label,
      kort: row.label_kort,
      fillRgba: hexNaarRgba(row.kleur_fill, row.fill_opacity),
      stroke: row.kleur_stroke,
      chipBg: hexNaarRgba(row.kleur_fill, 0.20),
      chipText: donkerderHex(row.kleur_stroke, 0.50),
      pattern: PATROON_MAP[row.patroon] || null,
      inverse: row.inverse_mask || false,
    };
  }
  return GEBIED_STIJL[type] || GEBIED_STIJL.overig;
}

// Diagonaal-strepenpatroon: dim grijze achtergrond + dikkere strepen, seamless.
export function maakDiagonaleStrepen(strokeColor = '#6e6e73') {
  if (typeof document === 'undefined') return null;
  const SIZE = 12;
  const c = document.createElement('canvas');
  c.width = SIZE;
  c.height = SIZE;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = 'rgba(110, 110, 115, 0.20)';
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.strokeStyle = strokeColor;
  ctx.globalAlpha = 0.7;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(-1, SIZE + 1);
  ctx.lineTo(SIZE + 1, -1);
  ctx.stroke();
  return ctx.createPattern(c, 'repeat');
}
